import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { candidatesApi, Candidate, candidateSavedViewsApi, CandidateSavedView, jobsApi, Job, pipelineStagesApi, type BulkImportMode, type BulkImportItem } from '../../lib/api';
import { buildCandidateSheetAoA, CANDIDATE_EXPORT_COLW } from '../../lib/candidateExport';
import CandidateFiltersModal from './CandidateFiltersModal';
import AiResumeSearch from './AiResumeSearch';
import { filterChipLabel, type CandidateFilterRow } from './candidateFilters';
import { useRBAC } from '../../contexts/RBACContext';
import PermissionGate from '../../components/PermissionGate';
import EmailComposer from '../../components/EmailComposer';
import { useToast } from '../../components/ToastHost';
import { useConfirm } from '../../components/ConfirmHost';
// Phase 2 design system
import {
  AuroraPage, PageHeader, Button, Card, Table, Modal, Field, Input, Select,
  Badge, EmptyState, Skeleton, Icon,
} from '../../components/ui';
import type { BadgeTone } from '../../components/ui';

// Server-side page size for the candidates list — keeps each load fast
// regardless of how many candidates the tenant has. Selectable per view; a
// bigger page lets "select all" cover more candidates in one mass action.
const PAGE_SIZES = [50, 100, 150] as const;
const DEFAULT_PAGE_SIZE = 50;
// Hard ceiling the server applies to "select all matching" (routes/candidates.ts
// BULK_MAX) — the most candidates one mass action can touch at once.
const SELECT_ALL_MAX = 1000;
// Export paging: the list endpoint caps a single request at 500 rows, so a mass
// export pages through with offset. EXPORT_PAGE is that per-request cap;
// EXPORT_MAX is the safety ceiling on one export (20 requests) so a huge tenant
// can't spin forever — the UI says when a result was capped.
const EXPORT_PAGE = 500;
const EXPORT_MAX = 10000;

// ─── CSV Import ───────────────────────────────────────────────────────────────
const CSV_TEMPLATE_HEADERS = ['first_name','last_name','email','phone','role','specialties','stage','recruiter_notes'];
const VALID_ROLES = ['RN','LPN','LVN','CNA','RT','NP','PA','Other'];

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''));
  return lines.slice(1).map(line => {
    const vals: string[] = [];
    let cur = '', inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else { cur += ch; }
    }
    vals.push(cur.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = vals[i] ?? ''; });
    return row;
  });
}

function downloadTemplate() {
  const header = CSV_TEMPLATE_HEADERS.join(',');
  const example = 'Jane,Smith,jane.smith@email.com,555-0100,RN,"ICU,PACU",application,Referred by nurse manager';
  const blob = new Blob([header + '\n' + example], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'candidates_import_template.csv'; a.click();
}

interface ImportResult { name: string; status: 'ok' | 'error'; message?: string; }

// Mass export → Excel. Pages through the list endpoint (capped at EXPORT_PAGE
// per request) for either the current filtered view or every candidate, then
// writes a .xlsx with one row per candidate. Contact details ride along because
// the list already returns them to anyone who can view candidates — this is the
// same data, just downloadable in bulk.
function ExportCandidatesModal({
  open, onClose, filterParams, hasActiveFilters, filteredTotal, selectedCandidates,
}: {
  open: boolean;
  onClose: () => void;
  filterParams: Record<string, unknown>;
  hasActiveFilters: boolean;
  filteredTotal: number;
  /** Full records for the rows the user has checked (current page). */
  selectedCandidates: Candidate[];
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [fetched, setFetched] = useState(0);

  function download(rows: Candidate[], scope: string) {
    const ws = XLSX.utils.aoa_to_sheet(buildCandidateSheetAoA(rows));
    ws['!cols'] = CANDIDATE_EXPORT_COLW;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `candidates-${scope}-${stamp}.xlsx`);
  }

  // Selected rows are already in memory — no fetch, just build and download.
  function runSelected() {
    if (busy) return;
    if (selectedCandidates.length === 0) { toast.info('No candidates selected.'); return; }
    download(selectedCandidates, 'selected');
    toast.success(`Exported ${selectedCandidates.length.toLocaleString()} selected candidate${selectedCandidates.length === 1 ? '' : 's'} to Excel.`);
    onClose();
  }

  async function run(scope: 'filtered' | 'all') {
    if (busy) return;
    setBusy(true);
    setFetched(0);
    try {
      const base = scope === 'filtered' ? filterParams : {};
      const all: Candidate[] = [];
      let offset = 0;
      let matchingTotal = Infinity;
      while (all.length < EXPORT_MAX) {
        const res = await candidatesApi.list({ ...base, limit: EXPORT_PAGE, offset });
        const rows = res.data?.candidates ?? [];
        matchingTotal = res.data?.total ?? all.length + rows.length;
        all.push(...rows);
        setFetched(all.length);
        if (rows.length < EXPORT_PAGE || all.length >= matchingTotal) break;
        offset += EXPORT_PAGE;
      }
      if (all.length === 0) {
        toast.info('No candidates to export.');
        return;
      }
      download(all, scope === 'filtered' ? 'filtered' : 'all');
      const capped = all.length >= EXPORT_MAX && matchingTotal > all.length;
      toast.success(
        `Exported ${all.length.toLocaleString()} candidate${all.length === 1 ? '' : 's'} to Excel` +
        (capped ? ` (capped at ${EXPORT_MAX.toLocaleString()} — narrow the filters to export the rest).` : '.')
      );
      onClose();
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const hasSelection = selectedCandidates.length > 0;

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }} title="Export candidates to Excel" size="sm">
      {busy ? (
        <div style={{ padding: '8px 0' }}>
          <p style={{ margin: 0, fontSize: 14 }}>
            Preparing your spreadsheet… {fetched > 0 ? `${fetched.toLocaleString()} loaded` : ''}
          </p>
          <p className="ui-table__muted" style={{ fontSize: 12.5, marginTop: 6 }}>
            The download starts automatically when it's ready.
          </p>
        </div>
      ) : (
        <>
          <p style={{ margin: '0 0 14px', fontSize: 14, lineHeight: 1.55 }}>
            Download candidates as an Excel (<code>.xlsx</code>) file — name, email, phone,
            role, stage, recruiter and location, one row each. Pick what to include:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Most specific choice first, as the primary button. */}
            {hasSelection && (
              <Button icon={<Icon name="download" size={15} />} onClick={runSelected}>
                Export selected ({selectedCandidates.length.toLocaleString()})
              </Button>
            )}
            {hasActiveFilters && (
              <Button
                variant={hasSelection ? 'secondary' : 'primary'}
                icon={<Icon name="download" size={15} />}
                onClick={() => run('filtered')}
              >
                Export current view{filteredTotal ? ` (${filteredTotal.toLocaleString()})` : ''}
              </Button>
            )}
            <Button
              variant={hasSelection || hasActiveFilters ? 'secondary' : 'primary'}
              icon={<Icon name="download" size={15} />}
              onClick={() => run('all')}
            >
              Export all candidates
            </Button>
          </div>
          <p className="ui-table__muted" style={{ fontSize: 12, marginTop: 14 }}>
            {hasSelection
              ? 'Tip: tick the rows you want first, or use a filter, so you only export who you need.'
              : 'Tip: tick rows or apply a filter first to export just those — or export everyone below.'}
            {' '}Up to {EXPORT_MAX.toLocaleString()} per export.
          </p>
        </>
      )}
    </Modal>
  );
}

function CSVImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<'upload' | 'importing' | 'done'>('upload');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [filename, setFilename] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadFile = (f: File) => {
    setFilename(f.name);
    setFile(f);
    // Client-side parse is for the row-count preview only. The import itself
    // sends the raw file to the server, whose parser handles quoted commas
    // (e.g. "Houston, Texas, United States") and the sourcing-export shape.
    const reader = new FileReader();
    reader.onload = e => {
      const parsed = parseCSV(e.target?.result as string ?? '');
      setRows(parsed);
    };
    reader.readAsText(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setStep('importing');
    try {
      // One request; the server parses, de-duplicates and reports per row.
      const res = await candidatesApi.bulkImport([file], { mode: 'none' });
      setResults((res.data.items ?? []).map((it) => ({
        name: it.name || it.file,
        status: it.status === 'created' || it.status === 'updated' || it.status === 'duplicate' ? 'ok' : 'error',
        message: it.status === 'updated' ? `Updated — filled in ${(it.filled ?? []).join(', ')}`
          : it.status === 'duplicate' ? 'Already on file with nothing missing'
          : it.reason,
      })));
    } catch (e: any) {
      setResults([{ name: filename || 'File', status: 'error', message: e?.response?.data?.error ?? 'Import failed' }]);
    }
    // Seed Education / Work Experience for anything imported before the
    // importer did it itself. Best-effort — never blocks the result screen.
    await candidatesApi.backfillRecords().catch(() => {});
    setStep('done');
    onDone();
  };

  const ok = results.filter(r => r.status === 'ok').length;
  const failed = results.filter(r => r.status === 'error').length;

  return (
    <Modal open onClose={onClose} title="Bulk Import Candidates" size="md">
      {step === 'upload' && (
        <>
          <div style={{ background: 'var(--sf2)', borderRadius: 'var(--r-md)', padding: '14px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--t2)' }}>
              <strong>Step 1:</strong> Upload a CSV. A sourcing-tool export works as-is — or download the template to build one by hand.
            </div>
            <Button size="sm" icon={<Icon name="download" size={14} />} onClick={downloadTemplate} style={{ whiteSpace: 'nowrap' }}>
              Template
            </Button>
          </div>

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) loadFile(f); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--pr)' : 'var(--bd)'}`,
              borderRadius: 'var(--r-lg)', padding: '40px 24px', textAlign: 'center',
              cursor: 'pointer', background: dragOver ? 'var(--brand-tint)' : 'var(--sf2)',
              transition: 'all 0.15s', marginBottom: 18,
            }}
          >
            <div style={{ color: 'var(--t3)', marginBottom: 10 }}><Icon name="upload" size={32} /></div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)', marginBottom: 4 }}>
              {filename ? filename : 'Drop CSV here or click to browse'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>Accepts .csv files</div>
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) loadFile(f); }} />
          </div>

          {rows.length > 0 && (
            <div style={{ background: 'var(--good-tint)', border: '1px solid var(--good-border)', borderRadius: 'var(--r-md)', padding: '12px 16px', marginBottom: 18, fontSize: 13, color: 'var(--good-fg)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="check-circle" size={16} />
              <span><strong>{rows.length} row{rows.length !== 1 ? 's' : ''}</strong> detected. Columns: {Object.keys(rows[0]).join(', ')}</span>
            </div>
          )}

          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleImport} disabled={!file}>
              Import {rows.length > 0 ? `${rows.length} Candidates` : ''}
            </Button>
          </Modal.Footer>
        </>
      )}

      {step === 'importing' && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ color: 'var(--pr)', marginBottom: 16 }}><Icon name="clock" size={36} /></div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--t1)' }}>Importing candidates…</div>
          <div style={{ fontSize: 13, color: 'var(--t3)', marginTop: 6 }}>Please wait, do not close this window.</div>
        </div>
      )}

      {step === 'done' && (
        <>
          <div style={{ background: failed === 0 ? 'var(--good-tint)' : 'var(--warn-tint)', borderRadius: 'var(--r-lg)', padding: 20, marginBottom: 20, textAlign: 'center' }}>
            <div style={{ marginBottom: 8, color: failed === 0 ? 'var(--good-fg)' : 'var(--warn-fg)' }}>
              <Icon name={failed === 0 ? 'check-circle' : 'alert'} size={36} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--t1)', marginBottom: 4 }}>Import Complete</div>
            <div style={{ fontSize: 14, color: 'var(--t3)' }}>
              <span style={{ color: 'var(--good-fg)', fontWeight: 700 }}>{ok} imported</span>
              {failed > 0 && <span style={{ color: 'var(--danger-fg)', fontWeight: 700, marginLeft: 12 }}>{failed} failed</span>}
            </div>
          </div>

          {failed > 0 && (
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 16 }}>
              {results.filter(r => r.status === 'error').map((r, i) => (
                <div key={i} style={{ background: 'var(--danger-tint)', borderRadius: 'var(--r-sm)', padding: '8px 12px', marginBottom: 6, fontSize: 13, color: 'var(--t2)' }}>
                  <strong style={{ color: 'var(--danger-fg)' }}>{r.name}</strong>: {r.message}
                </div>
              ))}
            </div>
          )}

          <Modal.Footer>
            <Button onClick={onClose}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

// ─── Mass Resume Import ─────────────────────────────────────────────────────
// Upload many resume PDFs/DOCX (or a .zip of them) at once. Before importing,
// choose how the whole batch is assigned to jobs.
const RESUME_ACCEPT = '.pdf,.docx,.txt,.csv,.zip';

function BulkResumeImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [step, setStep] = useState<'pick' | 'importing' | 'done'>('pick');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<BulkImportMode>('single');
  const [jobId, setJobId] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [items, setItems] = useState<BulkImportItem[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    jobsApi.list({ status: 'open' })
      .then((r) => setJobs(r.data.jobs ?? []))
      .catch(() => setJobs([]));
  }, []);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list).filter((f) => /\.(pdf|docx|txt|csv|zip)$/i.test(f.name));
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...picked.filter((f) => !seen.has(f.name + f.size))];
    });
  };

  const hasZip = files.some((f) => /\.zip$/i.test(f.name));
  const canImport =
    files.length > 0 && (mode !== 'single' || !!jobId) && step === 'pick';

  const handleImport = async () => {
    if (!canImport) return;
    setStep('importing');
    try {
      const res = await candidatesApi.bulkImport(files, { mode, job_id: mode === 'single' ? jobId : null });
      setItems(res.data.items ?? []);
      await candidatesApi.backfillRecords().catch(() => {});
      setStep('done');
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Bulk import failed');
      setStep('pick');
    }
  };

  const created = items.filter((i) => i.status === 'created').length;
  const updated = items.filter((i) => i.status === 'updated').length;
  const duplicates = items.filter((i) => i.status === 'duplicate').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const assigned = items.filter((i) => i.status === 'created' && i.jobId).length;

  return (
    <Modal open onClose={onClose} title="Mass import candidates" size="md">
      {step === 'pick' && (
        <>
          {/* File picker */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--brand)' : 'var(--bd)'}`,
              borderRadius: 'var(--r-md)', padding: '26px 18px', textAlign: 'center',
              cursor: 'pointer', background: dragOver ? 'var(--sf2)' : 'transparent', marginBottom: 14,
            }}
          >
            <Icon name="upload" size={22} />
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}>
              Drop resumes here, or click to choose
            </div>
            <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
              PDF, DOCX, TXT or CSV — many at once, or a single .zip of them
            </div>
            <input
              ref={fileRef} type="file" multiple accept={RESUME_ACCEPT}
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          {files.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {files.length} file{files.length === 1 ? '' : 's'} selected{hasZip ? ' (zip will be expanded)' : ''}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setFiles([])}>Clear</Button>
              </div>
              <div style={{ maxHeight: 120, overflowY: 'auto', fontSize: 12, color: 'var(--t2)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFiles((p) => p.filter((_, j) => j !== i)); }}
                      style={{ background: 'none', border: 'none', color: 'var(--t2)', cursor: 'pointer' }}
                      aria-label={`Remove ${f.name}`}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Job-assignment mode */}
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Assign these candidates to a job</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {([
              { v: 'single', label: 'Assign all to one job', hint: 'Every resume in this batch goes to the same job you pick.' },
              { v: 'match', label: 'Auto-match each to their best-fit job', hint: "Spread the batch across your open jobs by role, specialty and location — good when they aren't all for the same role." },
              { v: 'none', label: 'No job — add to the candidate pool', hint: 'Import everyone without a job; assign later.' },
            ] as { v: BulkImportMode; label: string; hint: string }[]).map((opt) => (
              <label key={opt.v} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
                border: `1px solid ${mode === opt.v ? 'var(--brand)' : 'var(--bd)'}`,
                borderRadius: 'var(--r-md)', cursor: 'pointer',
                background: mode === opt.v ? 'var(--sf2)' : 'transparent',
              }}>
                <input type="radio" name="assignMode" checked={mode === opt.v} onChange={() => setMode(opt.v)} style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--t2)' }}>{opt.hint}</div>
                </div>
              </label>
            ))}
          </div>

          {mode === 'single' && (
            <Field label="Job for the whole batch" required>
              <Select value={jobId} onChange={(e) => setJobId(e.target.value)}>
                <option value="">Select a job…</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title}{j.city || j.state ? ` — ${[j.city, j.state].filter(Boolean).join(', ')}` : ''}
                  </option>
                ))}
              </Select>
              {jobs.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
                  No open jobs found — create a job first, or choose auto-match / no job.
                </div>
              )}
            </Field>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button disabled={!canImport} onClick={handleImport} icon={<Icon name="upload" size={15} />}>
              Import {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : ''}
            </Button>
          </div>
        </>
      )}

      {step === 'importing' && (
        <div style={{ padding: '30px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Parsing resumes…</div>
          <div style={{ fontSize: 13, color: 'var(--t2)' }}>
            Reading and creating candidates. Large batches can take a minute — keep this open.
          </div>
        </div>
      )}

      {step === 'done' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Badge tone="good">{created} created</Badge>
            {updated > 0 && <Badge tone="info">{updated} updated with new info</Badge>}
            {assigned > 0 && <Badge tone="brand">{assigned} assigned to a job</Badge>}
            {duplicates > 0 && <Badge tone="neutral">{duplicates} already existed</Badge>}
            {failed > 0 && <Badge tone="danger">{failed} failed</Badge>}
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '6px 8px', borderBottom: '1px solid var(--bd)' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.name || it.file}
                  {it.jobTitle ? <span style={{ color: 'var(--t2)' }}> → {it.jobTitle}</span> : null}
                </span>
                <span style={{ whiteSpace: 'nowrap', color: it.status === 'created' ? 'var(--good)' : it.status === 'updated' ? 'var(--brand)' : it.status === 'duplicate' ? 'var(--t2)' : 'var(--danger)' }}>
                  {it.status === 'created' ? 'Created'
                    : it.status === 'updated' ? `Updated — ${(it.filled ?? []).join(', ')}`
                    : it.status === 'duplicate' ? 'Already complete'
                    : (it.reason || 'Failed')}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
            <Button onClick={onClose}>Done</Button>
          </div>
        </>
      )}
    </Modal>
  );
}

// Fallback stage vocabulary — the 12 pipeline stages seeded for a tenant
// (ats_phase1_migration). The tabs are normally driven by the live
// pipeline_stages API (per-tenant, customizable); this list is only used until
// that loads or if it can't be reached. It MUST match the real stage keys
// candidates are stored under — the old ['application','credentialing',
// 'onboarding'] set was remapped to new_lead/internal_review/confirmed, which
// is exactly why the old hardcoded tabs came up blank.
const FALLBACK_STAGES: { key: string; label: string; color?: string | null }[] = [
  { key: 'new_lead',         label: 'New Lead' },
  { key: 'screening',        label: 'Screening' },
  { key: 'internal_review',  label: 'Internal Review' },
  { key: 'submitted',        label: 'Submitted' },
  { key: 'client_submitted', label: 'Client Submitted' },
  { key: 'interview',        label: 'Interview' },
  { key: 'offer',            label: 'Offer' },
  { key: 'confirmed',        label: 'Confirmed' },
  { key: 'placed',           label: 'Placed' },
  { key: 'not_joined',       label: 'Not Joined' },
  { key: 'rejected',         label: 'Rejected' },
  { key: 'withdrawn',        label: 'Withdrawn' },
];

// Turn a stage key into a readable label when we have no better one from the
// API ("new_lead" → "New Lead").
function prettyStage(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Semantic tone per KNOWN pipeline stage — pairs the stage with a design-system
// tone so color never carries meaning alone. Stages outside this map fall back
// to neutral (or the stage's own configured color for the active pill).
const STAGE_TONE: Record<string, BadgeTone> = {
  new_lead:         'info',
  screening:        'info',
  internal_review:  'brand',
  submitted:        'brand',
  client_submitted: 'brand',
  interview:        'warn',
  offer:            'warn',
  confirmed:        'good',
  placed:           'good',
  not_joined:       'danger',
  rejected:         'danger',
  withdrawn:        'neutral',
};

// Solid token color for each tone — used to color the active stage-filter pill.
const TONE_SOLID: Record<BadgeTone, string> = {
  neutral: 'var(--t3)', brand: 'var(--pr)', good: 'var(--good)',
  warn: 'var(--warn)', danger: 'var(--danger)', info: 'var(--info)', outline: 'var(--t3)',
};

function StageBadge({ stage, label }: { stage: string; label?: string }) {
  return (
    <Badge tone={STAGE_TONE[stage] ?? 'neutral'}>
      {label || prettyStage(stage)}
    </Badge>
  );
}

// Compact chip for the recruiter-screening / HR-interview workflow columns.
function WorkflowChip({ kind, value, decision }: { kind: 'screening' | 'hr'; value?: string; decision?: string | null }) {
  let label = '—', tone: BadgeTone = 'neutral';
  if (kind === 'screening') {
    if (value === 'complete') { label = 'Complete'; tone = 'good'; }
    else if (value === 'in_progress') { label = 'In progress'; tone = 'warn'; }
    else { label = 'Not started'; tone = 'outline'; }
  } else {
    if (value === 'complete') {
      if (decision === 'approved') { label = 'Approved'; tone = 'good'; }
      else if (decision === 'rejected') { label = 'Rejected'; tone = 'danger'; }
      else { label = 'Complete'; tone = 'good'; }
    } else if (value === 'in_progress') { label = 'In progress'; tone = 'warn'; }
    else if (value === 'pending') { label = 'Pending'; tone = 'info'; }
    else { label = '—'; tone = 'outline'; }
  }
  return <Badge tone={tone}>{label}</Badge>;
}

export default function CandidateList() {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const downloadResume = async (id: string, filename: string) => {
    try {
      const resp = await candidatesApi.downloadResume(id);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'resume';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Failed to download resume.');
    }
  };
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);      // 0-based; server-paginated
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE); // rows per page (50/100/150)
  const [total, setTotal] = useState(0);    // total matching the current filters
  const [search, setSearch] = useState('');
  const [activeStage, setActiveStage] = useState('all');
  // Phase 1.1D — role + shift grouped filters
  const [roleFilter, setRoleFilter] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  // Quick filter for inbound website applications (source='website').
  const [webOnly, setWebOnly] = useState(false);
  // "Unassigned only" — candidates with no recruiter assigned.
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  // Exclude toggles for sourcing fresh leads.
  const [notContacted, setNotContacted] = useState(false);
  const [notInPipeline, setNotInPipeline] = useState(false);
  // Advanced "Edit Filters" (CEIPAL-style field/operator/value rows)
  const [advFilters, setAdvFilters] = useState<CandidateFilterRow[]>([]);
  // AI (natural-language) search box.
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showResumeImport, setShowResumeImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const { role, can } = useRBAC();

  // Bulk-select state. selectedIds is a Set for O(1) toggle. showBulkEnroll
  // pops the sequence-picker modal when the user clicks "Enroll in sequence"
  // in the floating action bar.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Contact info for selected candidates that are NOT on the current page (from
  // "select all matching"), so a mass EMAIL reaches the whole selection — not
  // just the rows currently loaded. Keyed by candidate id.
  const [selectedRecipients, setSelectedRecipients] = useState<Map<string, { email: string | null; name: string }>>(new Map());
  const [showBulkEnroll, setShowBulkEnroll] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [archivingSelected, setArchivingSelected] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  // Only selected candidates that actually have an address. The count is shown
  // on the button so a recruiter selecting 40 and reaching 37 knows before
  // sending, not after. Sources contact info from the current page AND the
  // cross-page cache (populated by "select all matching"), so a mass email
  // reaches every selected candidate, not just the ones on screen.
  const emailRecipients = (() => {
    const byId = new Map<string, { email: string | null; name: string }>();
    for (const [id, r] of selectedRecipients) byId.set(id, r);
    for (const c of candidates) byId.set(c.id, { email: c.email ?? null, name: `${c.first_name} ${c.last_name}` });
    const out: { address: string; candidateId: string; name: string }[] = [];
    for (const id of selectedIds) {
      const r = byId.get(id);
      if (r?.email) out.push({ address: r.email, candidateId: id, name: r.name });
    }
    return out;
  })();

  // Saved views (Phase 4)
  const [savedViews, setSavedViews] = useState<CandidateSavedView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [showSaveView, setShowSaveView] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  useEffect(() => {
    candidateSavedViewsApi.list()
      .then((r) => setSavedViews(r.data.views))
      .catch(() => { /* silent */ });
  }, []);

  // Pipeline stages drive the stage-filter tabs. They live in a per-tenant
  // table (pipeline_stages) that a migration remapped candidates onto, so the
  // tabs MUST come from here — a hardcoded list drifts out of sync with the
  // real stage keys and the tabs come up empty. Falls back to the seeded 12
  // until the API answers (or if it can't be reached).
  const [pipelineStages, setPipelineStages] =
    useState<{ key: string; label: string; color?: string | null }[]>(FALLBACK_STAGES);
  const [trackerStages, setTrackerStages] =
    useState<{ key: string; label: string; color?: string | null }[]>([]);
  useEffect(() => {
    pipelineStagesApi.list()
      .then((r) => {
        const active = (r.data.stages ?? [])
          .filter((s) => s.active)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((s) => ({ key: s.key, label: s.label, color: s.color }));
        if (active.length) setPipelineStages(active);
        const tracker = (r.data.tracker_stages ?? [])
          .filter((s) => s.active !== false)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((s) => ({ key: s.key, label: s.label, color: s.color }));
        setTrackerStages(tracker);
      })
      .catch(() => { /* keep the fallback list */ });
  }, []);

  // key → label for badges/tabs, from the live stages (fallback: prettified key).
  const knownStages = [...pipelineStages, ...trackerStages.filter((s) => !pipelineStages.some((p) => p.key === s.key))];
  const stageLabel = (key: string): string =>
    knownStages.find((s) => s.key === key)?.label ?? prettyStage(key);
  const stageColor = (key: string): string | null =>
    knownStages.find((s) => s.key === key)?.color ?? null;

  const applyView = (view: CandidateSavedView) => {
    setActiveViewId(view.id);
    const filters = view.filters as { search?: string; stage?: string; role?: string; shift?: string; advFilters?: CandidateFilterRow[] };
    if (filters.search !== undefined) setSearch(filters.search);
    if (filters.stage !== undefined) setActiveStage(filters.stage);
    if (filters.role !== undefined) setRoleFilter(filters.role);
    if (filters.shift !== undefined) setShiftFilter(filters.shift);
    setAdvFilters(Array.isArray(filters.advFilters) ? filters.advFilters : []);
  };

  const saveCurrentView = async () => {
    if (!newViewName.trim()) return;
    try {
      const res = await candidateSavedViewsApi.create(newViewName.trim(), {
        search, stage: activeStage, role: roleFilter, shift: shiftFilter, advFilters,
      });
      setSavedViews([res.data.view, ...savedViews]);
      setActiveViewId(res.data.view.id);
      setNewViewName('');
      setShowSaveView(false);
      toast.success(`Saved view "${res.data.view.name}".`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Failed to save view.');
    }
  };

  const deleteView = async (id: string) => {
    const ok = await confirm({
      title: 'Delete saved view?',
      description: 'This removes the view for you only. The candidates themselves are unaffected.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await candidateSavedViewsApi.delete(id);
      setSavedViews(savedViews.filter((v) => v.id !== id));
      if (activeViewId === id) setActiveViewId(null);
      toast.success('View deleted.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Failed to delete.');
    }
  };

  // One page at a time (server-paginated) so the page loads fast even when the
  // tenant has thousands of candidates. Filters/search reset to the first page.
  const fetchCandidates = async (pageArg = 0, sizeArg = pageSize) => {
    setLoading(true);
    setError(null);
    try {
      const params: { stage?: string; search?: string; role?: string; shift?: string; source?: string; filters?: string; unassigned?: string; not_contacted?: string; not_in_recruitment?: string; limit?: number; offset?: number } = {
        limit: sizeArg,
        offset: pageArg * sizeArg,
      };
      if (activeStage !== 'all') params.stage = activeStage;
      if (search.trim()) params.search = search.trim();
      if (roleFilter) params.role = roleFilter;
      if (shiftFilter) params.shift = shiftFilter;
      if (webOnly) params.source = 'website';
      if (unassignedOnly) params.unassigned = 'true';
      if (notContacted) params.not_contacted = 'true';
      if (notInPipeline) params.not_in_recruitment = 'true';
      if (advFilters.length) params.filters = JSON.stringify(advFilters);
      const res = await candidatesApi.list(params);
      setCandidates(res.data?.candidates ?? []);
      setTotal(res.data?.total ?? res.data?.candidates?.length ?? 0);
      setPage(pageArg);
      setSelectedIds(new Set()); // selection doesn't carry across pages
      setSelectedRecipients(new Map());
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load candidates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage, roleFilter, shiftFilter, webOnly, unassignedOnly, notContacted, notInPipeline, advFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCandidates(0);
  };

  // The filters currently on screen, so "select all matching" resolves exactly
  // the set the user is looking at.
  const currentFilterParams = () => {
    const params: Record<string, unknown> = {};
    if (activeStage !== 'all') params.stage = activeStage;
    if (search.trim()) params.search = search.trim();
    if (roleFilter) params.role = roleFilter;
    if (shiftFilter) params.shift = shiftFilter;
    if (webOnly) params.source = 'website';
    if (unassignedOnly) params.unassigned = 'true';
    if (notContacted) params.not_contacted = 'true';
    if (notInPipeline) params.not_in_recruitment = 'true';
    if (advFilters.length) params.filters = JSON.stringify(advFilters);
    return params;
  };

  // Natural-language search: ask the backend to translate the request into our
  // structured filters, then apply them to the list so the result is the live,
  // exportable, selectable set — not a copy/paste from a separate tool.
  const runAiSearch = async () => {
    const q = aiPrompt.trim();
    if (!q || aiBusy) return;
    setAiBusy(true);
    try {
      const { data } = await candidatesApi.smartFilter(q);
      const f = data.filter;
      // Map the AI's structured filter onto the page's filter state.
      setActiveStage(f.stage ?? 'all');
      setRoleFilter(f.role ?? '');
      setShiftFilter(f.shift ?? '');
      setUnassignedOnly(!!f.unassigned_only);
      setWebOnly(false);
      // The AI expresses "not contacted / not in pipeline" as advanced rows, so
      // clear the quick toggles to avoid a stale double-apply.
      setNotContacted(false);
      setNotInPipeline(false);
      const adv: CandidateFilterRow[] = (f.advanced ?? []).map((r) => ({ field: r.field, op: r.op, value: r.value }));
      // If the AI set a status, carry it as an advanced row (the list has no
      // standalone status control) so it still applies.
      if (f.status) adv.unshift({ field: 'status', op: 'is', value: f.status });
      setAdvFilters(adv);
      setActiveViewId(null);
      // The filter-state effect refetches; give the recruiter a read-back.
      toast.success(f.summary ? `Filtered: ${f.summary}` : 'Applied your search.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Could not run that search. Try rephrasing.');
    } finally {
      setAiBusy(false);
    }
  };

  // Select every candidate matching the current filters (across all pages), and
  // cache their name/email so a mass email/enroll reaches the whole set — not
  // just the page on screen. Capped at SELECT_ALL_MAX by the server.
  const handleSelectAllMatching = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      const res = await candidatesApi.list({ ...currentFilterParams(), limit: SELECT_ALL_MAX });
      const rows = res.data?.candidates ?? [];
      const matchingTotal = res.data?.total ?? rows.length;
      setSelectedIds(new Set(rows.map(c => c.id)));
      setSelectedRecipients(new Map(rows.map(c => [c.id, { email: c.email ?? null, name: `${c.first_name} ${c.last_name}` }])));
      if (matchingTotal > rows.length) {
        toast.info(`Selected the first ${rows.length.toLocaleString()} of ${matchingTotal.toLocaleString()} — that's the most that can be actioned at once.`);
      }
    } catch {
      toast.error('Could not select all matching candidates.');
    } finally {
      setSelectingAll(false);
    }
  };

  // Switch how many rows load per page (50 / 100 / 150) and reload from the top.
  const changePageSize = (n: number) => {
    if (n === pageSize) return;
    setPageSize(n);
    void fetchCandidates(0, n);
  };

  const clearSelection = () => { setSelectedIds(new Set()); setSelectedRecipients(new Map()); };

  // Archive and permanent delete differ only in wording and which action the
  // server performs, so they share one path — the reporting is the fiddly part
  // and it should not be written twice.
  const runBulk = async (action: 'archive' | 'permanent') => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const noun = `${count.toLocaleString()} candidate${count === 1 ? '' : 's'}`;

    const ok = await confirm(action === 'archive' ? {
      title: `Archive ${noun}?`,
      description: `This marks them as withdrawn and removes them from active lists. Documents, submissions, and audit history are kept.`,
      confirmLabel: `Archive ${noun}`,
      destructive: true,
    } : {
      title: `Delete ${noun} permanently?`,
      description: `This cannot be undone. Candidates with linked submissions, documents, or placements can't be deleted — those will be reported back so you can archive them instead.`,
      confirmLabel: `Delete ${noun} permanently`,
      destructive: true,
    });
    if (!ok) return;

    const setBusy = action === 'archive' ? setArchivingSelected : setDeletingSelected;
    setBusy(true);
    try {
      const { data } = await candidatesApi.bulk(Array.from(selectedIds), action);
      const verb = action === 'archive' ? 'Archived' : 'Permanently deleted';

      // Say what actually happened to all of them. The old version could report
      // success while some were silently skipped.
      const parts: string[] = [`${verb} ${data.succeeded.toLocaleString()}`];
      if (data.blocked) parts.push(`${data.blocked} could not be deleted (linked records) — archive those instead`);
      if (data.missing) parts.push(`${data.missing} were already gone`);
      const message = `${parts.join('. ')}.`;

      if (data.succeeded > 0 && !data.blocked && !data.missing) toast.success(message);
      else if (data.succeeded > 0) toast.info(message);
      else toast.error(data.blocked ? message : `Nothing was ${action === 'archive' ? 'archived' : 'deleted'}.`);

      const remainingTotal = Math.max(0, total - data.succeeded);
      const maxPage = Math.max(0, Math.ceil(remainingTotal / pageSize) - 1);
      await fetchCandidates(Math.min(page, maxPage));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.response?.data?.error
        ?? `Failed to ${action === 'archive' ? 'archive' : 'delete'} the selected candidates.`);
    } finally {
      setBusy(false);
    }
  };

  const handleBulkArchive = () => runBulk('archive');
  const handleBulkPermanentDelete = () => runBulk('permanent');

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const goToPage = (p: number) => { if (p >= 0 && p < pageCount && p !== page) fetchCandidates(p); };

  const hasActiveFilters = !!(search || roleFilter || shiftFilter || unassignedOnly || notContacted || notInPipeline || advFilters.length > 0);

  return (
    <AuroraPage>
      <PageHeader
        icon={<Icon name="candidates" size={22} />}
        title="Candidates"
        subtitle="Manage your recruiting pipeline"
        actions={
          <>
            <PermissionGate permission="candidates.view">
              <Button variant="secondary" icon={<Icon name="search" size={15} />} onClick={() => navigate('/bulk-lookup')}>
                Bulk Lookup
              </Button>
            </PermissionGate>
            <PermissionGate permission="candidates.view">
              <Button variant="secondary" icon={<Icon name="download" size={15} />} onClick={() => setShowExport(true)}>
                Export
              </Button>
            </PermissionGate>
            <PermissionGate permission="candidates.create">
              <Button variant="secondary" icon={<Icon name="upload" size={15} />} onClick={() => setShowImport(true)}>
                Import CSV
              </Button>
            </PermissionGate>
            <PermissionGate permission="candidates.create">
              <Button variant="secondary" icon={<Icon name="document" size={15} />} onClick={() => setShowResumeImport(true)}>
                Import Resumes
              </Button>
            </PermissionGate>
            <PermissionGate permission="candidates.create">
              <Button icon={<Icon name="plus" size={15} />} onClick={() => navigate('/candidates/new')}>
                New Candidate
              </Button>
            </PermissionGate>
          </>
        }
      />

      {/* AI Resume Search — ask for resumes, download them, scan vs a position */}
      <PermissionGate permission="candidates.view">
        <div style={{ marginBottom: 16 }}>
          <AiResumeSearch />
        </div>
      </PermissionGate>

      {/* Search + Stage Filters */}
      <Card>
        {/* Saved views row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Views</span>
          {savedViews.length === 0 && <span style={{ fontSize: 12, color: 'var(--t3)' }}>— none saved</span>}
          {savedViews.map((v) => (
            <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Button
                size="sm"
                variant={activeViewId === v.id ? 'primary' : 'secondary'}
                onClick={() => applyView(v)}
              >
                {v.name}
              </Button>
              <Button variant="ghost" size="sm" iconOnly aria-label={`Delete view ${v.name}`} title="Delete view" onClick={() => deleteView(v.id)}>
                <Icon name="x" size={13} />
              </Button>
            </span>
          ))}
          {!showSaveView ? (
            <Button variant="subtle" size="sm" icon={<Icon name="plus" size={13} />} onClick={() => setShowSaveView(true)}>
              Save current filters
            </Button>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Input
                autoFocus
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveCurrentView(); } }}
                placeholder="View name…"
                style={{ width: 150 }}
              />
              <Button size="sm" onClick={saveCurrentView}>Save</Button>
              <Button variant="ghost" size="sm" onClick={() => { setShowSaveView(false); setNewViewName(''); }}>Cancel</Button>
            </span>
          )}
        </div>

        {/* AI natural-language search — describe requirements in plain English
            and it fills the filters for you. */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 320px' }}>
            <Input
              icon={<Icon name="sparkles" size={16} />}
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runAiSearch(); } }}
              placeholder="Describe who you need — e.g. “RN, ICU, Houston, contract, unassigned, 2+ yrs, not contacted yet”"
            />
          </div>
          <Button variant="secondary" icon={<Icon name="sparkles" size={15} />} loading={aiBusy} disabled={!aiPrompt.trim()} onClick={() => void runAiSearch()}>
            AI Find
          </Button>
        </div>

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 260px' }}>
            <Input
              icon={<Icon name="search" size={16} />}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, role…"
            />
          </div>
          {/* Phase 1.1D — position (role) + shift dropdowns, grouped visually
              next to the search box. Selecting a value triggers a re-fetch
              immediately (no Search click needed for these). */}
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            title="Filter by position / role (RN, LPN, etc.)"
            style={{ minWidth: 140 }}
          >
            <option value="">All positions</option>
            {['RN', 'LPN', 'LVN', 'CNA', 'RT', 'NP', 'PA', 'Other'].map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Select
            value={shiftFilter}
            onChange={(e) => setShiftFilter(e.target.value)}
            title="Filter by available shift — matches candidate.available_shifts"
            style={{ minWidth: 140 }}
          >
            <option value="">All shifts</option>
            {['days', 'evenings', 'nights', 'weekends', 'rotating'].map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Button type="submit">Search</Button>
          <Button
            type="button"
            variant={advFilters.length ? 'primary' : 'secondary'}
            icon={<Icon name="filter" size={15} />}
            onClick={() => setShowFilters(true)}
            title="Advanced filters"
          >
            Filters{advFilters.length ? ` (${advFilters.length})` : ''}
          </Button>
          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setSearch(''); setRoleFilter(''); setShiftFilter(''); setAdvFilters([]); }}
              title="Clear all filters"
            >
              Clear
            </Button>
          )}
        </form>

        {/* Active advanced-filter chips */}
        {advFilters.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {advFilters.map((f, i) => (
              <Badge key={i} tone="brand">
                {filterChipLabel(f)}
                <button
                  onClick={() => setAdvFilters((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label="Remove filter"
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0, marginLeft: 4, display: 'inline-flex' }}
                >
                  <Icon name="x" size={12} />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {showFilters && (
          <CandidateFiltersModal
            initial={advFilters}
            onApply={(rows) => { setAdvFilters(rows); setActiveViewId(null); }}
            onClose={() => setShowFilters(false)}
          />
        )}

        {/* Workflow hint bar — every role that uses this page benefits from
            the "use Pipeline to move candidates" reminder. */}
        {(role === 'hr' || role === 'coordinator' || role === 'manager' || role === 'recruiter') && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: 'var(--info-tint)',
            border: '1px solid var(--info-border)',
            borderRadius: 'var(--r-md)',
            padding: '7px 14px',
            marginBottom: 12,
            gap: 0,
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginRight: 10 }}>Pipeline:</span>
            {pipelineStages.map((s, idx, arr) => (
              <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <button
                  onClick={() => setActiveStage(s.key)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: activeStage === s.key ? 700 : 500,
                    color: activeStage === s.key ? 'var(--info-fg)' : 'var(--t3)',
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontFamily: 'inherit',
                    textDecoration: activeStage === s.key ? 'underline' : 'none',
                    transition: 'color 0.13s',
                  }}
                >
                  {s.label}
                </button>
                {idx < arr.length - 1 && (
                  <Icon name="chevron-right" size={13} style={{ color: 'var(--info-border)' }} />
                )}
              </span>
            ))}
          </div>
        )}

        {/* Stage tabs — driven by the live pipeline_stages list so they always
            match the stage keys candidates are actually stored under. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['all', ...pipelineStages.map((s) => s.key), ...trackerStages.filter((s) => !pipelineStages.some((p) => p.key === s.key)).map((s) => s.key)].map((stage) => {
            const isActive = activeStage === stage;
            const activeBg = stage === 'all'
              ? 'var(--pr)'
              : (stageColor(stage) ?? TONE_SOLID[STAGE_TONE[stage] ?? 'neutral']);
            return (
              <button
                key={stage}
                onClick={() => setActiveStage(stage)}
                style={{
                  padding: '5px 14px',
                  borderRadius: 20,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  background: isActive ? activeBg : 'var(--sf3)',
                  color: isActive ? '#fff' : 'var(--t3)',
                }}
              >
                {stage === 'all' ? 'All' : stageLabel(stage)}
              </button>
            );
          })}
          <button
            onClick={() => setWebOnly((v) => !v)}
            title="Candidates who applied through the website"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: webOnly ? 700 : 500,
              background: webOnly ? 'var(--good)' : 'var(--sf3)', color: webOnly ? '#fff' : 'var(--t3)',
            }}
          >
            <Icon name="external" size={13} /> Web Applicants
          </button>
          <button
            onClick={() => setUnassignedOnly((v) => !v)}
            title="Candidates with no recruiter assigned yet"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: unassignedOnly ? 700 : 500,
              background: unassignedOnly ? 'var(--info)' : 'var(--sf3)', color: unassignedOnly ? '#fff' : 'var(--t3)',
            }}
          >
            <Icon name="user" size={13} /> Unassigned
          </button>
          <button
            onClick={() => setNotContacted((v) => !v)}
            title="Hide candidates we've already reached out to (call, message, or email sequence)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: notContacted ? 700 : 500,
              background: notContacted ? 'var(--warn)' : 'var(--sf3)', color: notContacted ? '#fff' : 'var(--t3)',
            }}
          >
            <Icon name="phone" size={13} /> Not contacted
          </button>
          <button
            onClick={() => setNotInPipeline((v) => !v)}
            title="Hide candidates already progressing through recruitment (in a working stage or submitted to a job)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: notInPipeline ? 700 : 500,
              background: notInPipeline ? 'var(--warn)' : 'var(--sf3)', color: notInPipeline ? '#fff' : 'var(--t3)',
            }}
          >
            <Icon name="filter" size={13} /> Not in pipeline
          </button>
        </div>
      </Card>

      {/* Table */}
      <div style={{ marginTop: 'var(--sp-5)' }}>
        {loading ? (
          <Card padded={false}>
            <div style={{ padding: 20, display: 'grid', gap: 12 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <Skeleton variant="circle" width={16} height={16} />
                  <Skeleton width="22%" />
                  <Skeleton width="10%" />
                  <Skeleton width="14%" />
                  <Skeleton width="12%" />
                  <Skeleton width="12%" />
                </div>
              ))}
            </div>
          </Card>
        ) : (error || candidates.length === 0) && candidates.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Icon name="user" size={26} />}
              title="No candidates found"
              description={activeStage !== 'all' || search ? 'Try adjusting your filters.' : 'Add your first candidate to get started.'}
              action={
                <Button icon={<Icon name="plus" size={15} />} onClick={() => navigate('/candidates/new')}>
                  Add Candidate
                </Button>
              }
            />
          </Card>
        ) : (
          <Card padded={false} title="Candidates" actions={<Badge tone="neutral">{total.toLocaleString()} total</Badge>}>
            <Table hover framed={false}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      checked={candidates.length > 0 && candidates.every(c => selectedIds.has(c.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(candidates.map(c => c.id)));
                        } else {
                          clearSelection();
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Select all on this page"
                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--pr)' }}
                    />
                  </th>
                  {['Name', 'Role', 'Phone', 'Stage', 'Screening', 'HR', 'Recruiter', 'Updated', 'Actions'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const checked = selectedIds.has(c.id);
                  return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/candidates/${c.id}`)}
                    style={{ cursor: 'pointer', background: checked ? 'var(--brand-tint)' : undefined }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                            return next;
                          });
                        }}
                        aria-label={`Select ${c.first_name} ${c.last_name}`}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--pr)' }}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--t1)', fontSize: 14 }}>
                        {c.first_name} {c.last_name}
                      </div>
                      {c.email && (
                        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>{c.email}</div>
                      )}
                    </td>
                    <td style={{ fontSize: 14, color: 'var(--t2)' }}>
                      {c.role ?? '—'}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--t2)' }}>
                      {c.phone ?? '—'}
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <StageBadge stage={c.stage} label={stageLabel(c.stage)} />
                        {(c.missing_docs_count ?? 0) > 0 && (
                          <Badge tone="warn">
                            {c.missing_docs_count} missing doc{c.missing_docs_count !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td><WorkflowChip kind="screening" value={c.recruiter_screening_status} /></td>
                    <td><WorkflowChip kind="hr" value={c.hr_interview_status} decision={c.hr_decision} /></td>
                    <td style={{ fontSize: 14, color: 'var(--t2)' }}>
                      {c.recruiter_name ?? '—'}
                    </td>
                    <td style={{ fontSize: 14, color: 'var(--t2)' }}>
                      {c.days_since_update != null ? `${c.days_since_update}d ago` : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <Button variant="secondary" size="sm" onClick={() => navigate(`/candidates/${c.id}`)}>
                          View
                        </Button>
                        {c.resume_filename && (
                          <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            aria-label="Download resume"
                            title="Download resume"
                            onClick={() => void downloadResume(c.id, c.resume_filename!)}
                          >
                            <Icon name="document" size={15} />
                          </Button>
                        )}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
            {total > PAGE_SIZES[0] && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderTop: '1px solid var(--bd)', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--t3)' }}>
                  {(page * pageSize + 1).toLocaleString()}–{Math.min((page + 1) * pageSize, total).toLocaleString()} of {total.toLocaleString()}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>Show</span>
                    <select
                      value={pageSize}
                      onChange={(e) => changePageSize(Number(e.target.value))}
                      disabled={loading}
                      aria-label="Candidates per page"
                      style={{
                        fontSize: 13, padding: '4px 8px', borderRadius: 8,
                        border: '1px solid var(--bd)', background: 'var(--sf)', color: 'var(--t1)',
                        cursor: loading ? 'default' : 'pointer',
                      }}
                    >
                      {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>per page</span>
                  </span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button variant="secondary" size="sm" disabled={loading || page <= 0} onClick={() => goToPage(page - 1)} icon={<Icon name="chevron-left" size={14} />}>Prev</Button>
                  <span style={{ fontSize: 13, color: 'var(--t2)' }}>Page {page + 1} of {pageCount}</span>
                  <Button variant="secondary" size="sm" disabled={loading || page >= pageCount - 1} onClick={() => goToPage(page + 1)} icon={<Icon name="chevron-right" size={14} />}>Next</Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {showImport && (
        <CSVImportModal
          onClose={() => setShowImport(false)}
          onDone={() => { fetchCandidates(); }}
        />
      )}

      {showResumeImport && (
        <BulkResumeImportModal
          onClose={() => setShowResumeImport(false)}
          onDone={() => { fetchCandidates(); }}
        />
      )}

      <ExportCandidatesModal
        open={showExport}
        onClose={() => setShowExport(false)}
        filterParams={currentFilterParams()}
        hasActiveFilters={hasActiveFilters}
        filteredTotal={total}
        selectedCandidates={candidates.filter((c) => selectedIds.has(c.id))}
      />

      {/* Floating bulk-action bar — only when >=1 candidate is selected */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          emailableCount={emailRecipients.length}
          canEmail={can('email.send')}
          canArchive={can('candidates_delete')}
          matchingTotal={total}
          canSelectAllMatching={
            // Only worth offering once the page is full and there is more behind it.
            candidates.length > 0 && selectedIds.size >= candidates.length && total > candidates.length
          }
          selectingAll={selectingAll}
          onSelectAllMatching={() => void handleSelectAllMatching()}
          archiving={archivingSelected}
          deleting={deletingSelected}
          onArchive={() => void handleBulkArchive()}
          onPermanentDelete={() => void handleBulkPermanentDelete()}
          onClear={clearSelection}
          onEnrollSequence={() => setShowBulkEnroll(true)}
          onEmail={() => setShowBulkEmail(true)}
        />
      )}

      {showBulkEmail && (
        <EmailComposer
          open={showBulkEmail}
          onClose={() => setShowBulkEmail(false)}
          recipients={emailRecipients}
          onSent={() => { setShowBulkEmail(false); clearSelection(); }}
        />
      )}

      {showBulkEnroll && (
        <BulkEnrollSequenceModal
          candidateIds={Array.from(selectedIds)}
          onClose={() => setShowBulkEnroll(false)}
          onDone={() => { setShowBulkEnroll(false); clearSelection(); }}
        />
      )}
    </AuroraPage>
  );
}

function BulkActionBar({
  count, emailableCount, canEmail, canArchive, archiving, deleting,
  matchingTotal, canSelectAllMatching, selectingAll,
  onArchive, onPermanentDelete, onClear, onEnrollSequence, onEmail, onSelectAllMatching,
}: {
  count: number;
  emailableCount: number;
  canEmail: boolean;
  canArchive: boolean;
  archiving: boolean;
  deleting: boolean;
  matchingTotal: number;
  canSelectAllMatching: boolean;
  selectingAll: boolean;
  onArchive: () => void;
  onPermanentDelete: () => void;
  onClear: () => void;
  onEnrollSequence: () => void;
  onEmail: () => void;
  onSelectAllMatching: () => void;
}) {
  const busy = archiving || deleting || selectingAll;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--t1)', color: 'var(--sf)', borderRadius: 999,
      padding: '10px 18px',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      zIndex: 50, fontSize: 13,
    }}>
      <span style={{ fontWeight: 700 }}>{count.toLocaleString()} selected</span>
      {/* Selection stops at the page boundary, which is what made bulk actions
          feel broken: you clear 50, the page refills from behind, and the list
          looks untouched. Offer the whole matching set explicitly. */}
      {canSelectAllMatching && (
        <button
          type="button"
          onClick={onSelectAllMatching}
          disabled={busy}
          style={{
            background: 'none', border: 0, padding: 0, color: 'var(--sf)',
            fontSize: 12.5, fontWeight: 600, textDecoration: 'underline',
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          }}
        >
          {selectingAll ? 'Selecting…' : `Select all ${matchingTotal.toLocaleString()} matching`}
        </button>
      )}
      {canEmail && (
        <Button
          size="sm"
          icon={<Icon name="mail" size={14} />}
          onClick={onEmail}
          disabled={emailableCount === 0 || busy}
          // Say up front how many of the selection can actually be reached —
          // silently dropping the rest would misreport what was sent.
          title={emailableCount === count
            ? `Send an individual email to each of the ${count} selected candidates`
            : `${emailableCount} of ${count} selected candidates have an email address on file`}
        >
          {emailableCount === count ? 'Email' : `Email (${emailableCount})`}
        </Button>
      )}
      <Button variant="secondary" size="sm" icon={<Icon name="arrow-right" size={14} />} onClick={onEnrollSequence} disabled={busy}>
        Enroll in sequence
      </Button>
      {canArchive && (
        <Button variant="danger" size="sm" onClick={onArchive} loading={archiving} disabled={deleting}>
          {archiving ? 'Archiving…' : 'Archive selected'}
        </Button>
      )}
      {canArchive && (
        <Button variant="danger" size="sm" onClick={onPermanentDelete} loading={deleting} disabled={archiving}>
          {deleting ? 'Deleting…' : 'Delete permanently'}
        </Button>
      )}
      <Button variant="secondary" size="sm" onClick={onClear} disabled={busy}>
        Clear
      </Button>
    </div>
  );
}

function BulkEnrollSequenceModal({
  candidateIds, onClose, onDone,
}: {
  candidateIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [sequences, setSequences] = useState<Array<{ id: string; name: string; description: string | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [pickedId, setPickedId] = useState<string>('');
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { sequencesApi } = await import('../../lib/api');
        const r = await sequencesApi.list();
        if (cancelled) return;
        setSequences(r.data.sequences.map(s => ({ id: s.id, name: s.name, description: s.description ?? null })));
      } catch {
        if (!cancelled) toast.error('Failed to load sequences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  async function enroll() {
    if (!pickedId) return;
    setEnrolling(true);
    try {
      const { sequencesApi } = await import('../../lib/api');
      const r = await sequencesApi.enrollBulk(pickedId, candidateIds);
      if (r.data.failed === 0) {
        toast.success(`Enrolled ${r.data.enrolled} candidates`);
      } else {
        toast.info(`Enrolled ${r.data.enrolled}, skipped ${r.data.failed} (already enrolled or ineligible)`);
      }
      onDone();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Bulk enroll failed';
      toast.error(msg);
    } finally {
      setEnrolling(false);
    }
  }

  const pickedDescription = sequences.find(s => s.id === pickedId)?.description;

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={
        <span>
          <span style={{ display: 'block' }}>Enroll in sequence</span>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--t3)', marginTop: 2 }}>
            {candidateIds.length} candidate{candidateIds.length === 1 ? '' : 's'} will start the chosen drip.
          </span>
        </span>
      }
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading sequences…</div>
      ) : sequences.length === 0 ? (
        <div style={{ padding: 16, background: 'var(--warn-tint)', color: 'var(--warn-fg)', borderRadius: 'var(--r-md)', fontSize: 13 }}>
          No active sequences. Create one first under <strong>/sequences</strong>.
        </div>
      ) : (
        <>
          <Field label="Sequence">
            <Select value={pickedId} onChange={(e) => setPickedId(e.target.value)}>
              <option value="">— Pick a sequence —</option>
              {sequences.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
          </Field>
          {pickedId && pickedDescription && (
            <div style={{ marginTop: 8, padding: 10, background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--t2)' }}>
              {pickedDescription}
            </div>
          )}
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose} disabled={enrolling}>
              Cancel
            </Button>
            <Button onClick={() => void enroll()} disabled={enrolling || !pickedId} loading={enrolling}>
              {enrolling ? 'Enrolling…' : `Enroll ${candidateIds.length}`}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}
